/* eslint-disable import/extensions */
/* eslint-disable object-curly-newline */
/* eslint-disable camelcase */
import React, { useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import { Typography } from '@telus/koodo-ui-kit'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  getSimSelectionPageContent,
  getSimSelectionSlugValue
} from '@apps/utils/simSelectionContent'
import { setPageAnalytics } from '@apps/utils/Analytics'
import { ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import useIsMobileScreen from '@/apps/hooks/isMobileScreen'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

const BUTTON_LABEL_TOKENS = {
  color: '#ffffff',
  fontName: 'StagSans',
  fontSize: 16,
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: '20px'
}
const HEADING_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 20,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '28px'
}
const OR_LABEL_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '24px'
}
const MESSAGE_COPY_TOKENS = {
  color: '#000000',
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: '20px'
}
const MESSAGE_BOLD_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '20px'
}

const MarkdownParagraph = ({ children }) => (
  <div className="m-0">
    <Typography
      tag="p"
      variant={{ size: 14, color: '#000000' }}
      tokens={MESSAGE_COPY_TOKENS}
    >
      {children}
    </Typography>
  </div>
)

MarkdownParagraph.propTypes = {
  children: PropTypes.node
}

const MarkdownStrong = ({ children }) => (
  <Typography
    tag="strong"
    variant={{ size: 14, bold: true, color: '#000000' }}
    tokens={MESSAGE_BOLD_TOKENS}
  >
    {children}
  </Typography>
)

MarkdownStrong.propTypes = {
  children: PropTypes.node
}

const getMarkdownText = (children) => React.Children.toArray(children)
  .map((child) => (typeof child === 'string' ? child : ''))
  .join('')

const MarkdownLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-id={getMarkdownText(children)}
    className="text-black underline"
  >
    <Typography
      tag="span"
      variant={{ size: 14, bold: true, color: '#000000' }}
      tokens={MESSAGE_BOLD_TOKENS}
    >
      {children}
    </Typography>
  </a>
)

MarkdownLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node
}

const purchaseMessageMarkdownComponents = {
  p: MarkdownParagraph,
  strong: MarkdownStrong,
  a: MarkdownLink
}

const GetNewPSimCard = ({ userType = '' }) => {
  const { pageContent } = useSelector((state) => state.common.content)
  const location = useLocation()
  const isMobileScreen = useIsMobileScreen()
  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region
  } = useContext(ApplicationContext)

  const tagManagerInfo = useTagManagerInfo()

  const pushClickAnalytics = ({ click_text, journey_type, event_type, target_link, event_click = ANALYTICS_EVENT_TYPES.CLICK } = {}) => {

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(location, 'click', region, language, {
        ...tagManagerInfo,
        click_text,
        journey_type
      }, event_type, event_click, target_link)
    )
  }

  const simSelectionPageContent = getSimSelectionPageContent(pageContent, language, userType)
  const { shortTextFields, longTextFields } = simSelectionPageContent

  const pageTextContent = useMemo(() => {
    if (!shortTextFields && !longTextFields) return {}
    return {
      locateStore: getSimSelectionSlugValue(shortTextFields, 'locateStore', userType),
      orderLink: getSimSelectionSlugValue(shortTextFields, 'orderLink', userType),
      orVisit: getSimSelectionSlugValue(shortTextFields, 'orVisit', userType),
      storeLink: getSimSelectionSlugValue(shortTextFields, 'storeLink', userType),
      purchasePhysicalSIMHeading:
        getSimSelectionSlugValue(shortTextFields, 'purchasePhysicalSIMHeading', userType),
      purchasePhysicalSIMMessage:
        getSimSelectionSlugValue(
          longTextFields,
          'purchasePhysicalSIMMessage',
          userType,
          'longTextFields'
        ),
      orderOnline: getSimSelectionSlugValue(shortTextFields, 'orderOnline', userType)
    }
  }, [shortTextFields, longTextFields, userType])

  const orderSimAnalytics = () => {
    pushClickAnalytics({ click_text: pageTextContent?.orderOnline, journey_type: 'pSIM', event_type: 'Order physical sim online', target_link: pageTextContent?.orderLink })
  }

  const locateStoreAnalytics = () => {
    pushClickAnalytics({ click_text: pageTextContent?.locateStore, journey_type: 'pSIM', event_type: 'locate store', target_link: pageTextContent?.storeLink })
  }

  const renderButton = (label, href, onClick) => (
    <a
      className={`inline-flex min-h-[44px] min-w-[140px] items-center justify-center rounded bg-black px-[18px] text-white no-underline hover:text-white focus:text-white ${isMobileScreen ? 'w-full' : 'w-auto'}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      <Typography
        variant={{ size: 14, bold: true, color: '#ffffff' }}
        tokens={BUTTON_LABEL_TOKENS}
      >
        {label}
      </Typography>
    </a>
  )

  return (
    <div>
      <div
        className="mb-4"
        data-contentful-slug="purchasePhysicalSIMHeading"
      >
        <Typography
          variant={{ size: 20, bold: true, color: '#000000' }}
          tokens={HEADING_TOKENS}
        >
          {pageTextContent?.purchasePhysicalSIMHeading}
        </Typography>
      </div>
      <div
        className="mb-7 bg-[#f2f2f2] px-4 py-3 text-black"
        data-contentful-slug="purchasePhysicalSIMMessage"
      >
        <ReactMarkdown components={purchaseMessageMarkdownComponents}>
          {pageTextContent?.purchasePhysicalSIMMessage || ''}
        </ReactMarkdown>
      </div>
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
        {renderButton(pageTextContent?.orderOnline, pageTextContent?.orderLink, orderSimAnalytics)}
        <div className="w-full text-center sm:w-auto sm:text-left">
          <Typography
            variant={{ size: 16, bold: true, color: '#000000' }}
            tokens={OR_LABEL_TOKENS}
          >
            {pageTextContent?.orVisit}
          </Typography>
        </div>
        {renderButton(pageTextContent?.locateStore, pageTextContent.storeLink, locateStoreAnalytics)}
      </div>
    </div>
  )
}

GetNewPSimCard.propTypes = {
  userType: PropTypes.string
}

export default GetNewPSimCard
